// ============================================================================
// execUtils.js — Utilitários PRÓPRIOS da VIEW EXECUTIVA (Sales Day 2.0)
// ----------------------------------------------------------------------------
// IMPORTANTE: este arquivo é ISOLADO. NÃO importa nada de SalesDay2Dashboard.js
// (cujos helpers não são exportados). Os formatadores triviais abaixo são
// DUPLICADOS de propósito para manter a tela atual 100% intocada.
// COMPANIES é reexportado de '../../App' (lá já é export const).
// ============================================================================

import { COMPANIES } from '../../App';

export { COMPANIES };

// As 4 lojas operacionais (mesmo filtro do dashboard atual).
// LAZY (função): execUtils faz parte de um ciclo de import com App.js
// (App -> SalesDayViewSwitcher -> ... -> execUtils -> App). Avaliar
// COMPANIES.filter() no topo do módulo causaria TDZ (COMPANIES ainda não
// inicializado). Chamar getOperationalCompanies() só em runtime resolve.
export const getOperationalCompanies = () =>
  COMPANIES.filter((c) => !c.isSalesDay && !c.isWarromComp && !c.isSalesDay2);

// Tema de cor por loja (DUPLICADO de COMPANY_THEME do dashboard — não exportado lá).
export const COMPANY_THEME = {
  jmv: { color: '#0f766e' },
  'fix-csp': { color: '#b45309' },
  'fix-msc': { color: '#9f1239' },
  vip: { color: '#047857' },
};

// Fonte mono (duplicada).
export const monoFont = `'JetBrains Mono', 'SF Mono', ui-monospace, Menlo, monospace`;

// ===== Formatadores (duplicados dos triviais do dashboard) =====
export const fmtNum = (n) => new Intl.NumberFormat('pt-BR').format(n || 0);
export const fmtBRL = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
// fmtPct com 1 casa decimal (ex: 87.5%).
export const fmtPct = (n) => `${(Number.isFinite(n) ? n : 0).toFixed(1)}%`;

// ===== Datas (mesma lógica do dashboard) =====
export const fmtDate = (d) => d.toISOString().split('T')[0];
export const fmtDateBR = (s) => {
  if (!s) return '';
  const [y, m, d] = s.split('-');
  return `${d}/${m}/${y}`;
};

// ===== Cores de status =====
// corPctMeta(value, expected) — MESMA lógica do dashboard atual:
//   verde se value>=expected, amarelo se >=85% do expected, vermelho abaixo.
export function corPctMeta(value, expected) {
  if (value >= expected) return '#10b981';
  if (value >= expected * 0.85) return '#facc15';
  return '#ef4444';
}

// pctColor(pct) — atingimento percentual (0-100+):
//   verde >=100%, amarelo 70-99%, vermelho <70%.
export function pctColor(pct) {
  if (pct >= 100) return '#10b981';
  if (pct >= 70) return '#facc15';
  return '#ef4444';
}

// statusFromPct(pct) — rótulo + cor a partir do score/atingimento (0-100).
//   excelente >=85, bom >=70, atenção >=50, crítico <50.
export function statusFromPct(pct) {
  const p = Number.isFinite(pct) ? pct : 0;
  if (p >= 85) return { key: 'excelente', label: 'Excelente', cor: '#10b981' };
  if (p >= 70) return { key: 'bom', label: 'Bom', cor: '#34d399' };
  if (p >= 50) return { key: 'atencao', label: 'Atenção', cor: '#facc15' };
  return { key: 'critico', label: 'Crítico', cor: '#ef4444' };
}

// Clamp helper para sub-scores 0-100.
export const clamp = (n, min = 0, max = 100) =>
  Math.max(min, Math.min(max, Number.isFinite(n) ? n : 0));

// ===== Proração (MESMO cálculo do dashboard) =====
// diasNoPeriodo = (dtFim - dtIni)/dia + 1 ; diasNoMes = último dia do mês de dtIni.
export function periodDays(dataInicio, dataFim) {
  const dtIni = new Date(dataInicio + 'T00:00:00');
  const dtFim = new Date(dataFim + 'T00:00:00');
  const diasNoPeriodo = Math.round((dtFim - dtIni) / 86400000) + 1;
  const diasNoMes = new Date(dtIni.getFullYear(), dtIni.getMonth() + 1, 0).getDate();
  return { diasNoPeriodo, diasNoMes, dtIni, dtFim };
}

// ===== Projeção por DIAS ÚTEIS (seg-sex) =====
// Conta dias úteis (segunda a sexta) no intervalo [inicio, fim] inclusive.
// (Feriados não são descontados — pode evoluir com uma lista de feriados.)
// ===== FERIADOS NACIONAIS (não contam como dia útil) =====
// Editar AQUI vale para o sistema inteiro: "esperado até hoje" de todos os
// slides/cards e dias úteis da produtividade. (Pedido FIX CSP 21/09: 07/09
// não estava sendo descontado do esperado.)
export const FERIADOS_FIXOS = [
  '01-01', // Confraternização Universal
  '04-21', // Tiradentes
  '05-01', // Dia do Trabalho
  '09-07', // Independência
  '10-12', // N. Sra. Aparecida
  '11-02', // Finados
  '11-15', // Proclamação da República
  '11-20', // Consciência Negra
  '12-25', // Natal
];
export const FERIADOS_MOVEIS = [
  '2026-04-03', // Sexta-feira Santa
  '2027-03-26', // Sexta-feira Santa
  // Carnaval e Corpus Christi são ponto facultativo — adicionar aqui se as lojas fecharem.
];
// Feriados LOCAIS (municipais + estaduais) por loja, em MM-DD — cada cidade tem
// dias úteis diferentes (ex.: 08/09 é feriado em Curitiba, mas não em POA/SP).
// EDITAR SÓ AQUI quando incluir loja nova ou corrigir a lista de uma cidade.
export const FERIADOS_LOCAIS = {
  jmv: ['01-25', '07-09'],           // São Paulo/SP: Aniversário da cidade; Revolução Constitucionalista (estadual)
  'fix-csp': ['02-02', '09-20'],     // Porto Alegre/RS: N. Sra. dos Navegantes; Revolução Farroupilha (estadual)
  'fix-msc': ['02-02', '09-20'],     // Porto Alegre/RS (mesma cidade da CSP)
  vip: ['09-08', '12-19'],           // Curitiba/PR: N. Sra. da Luz (padroeira); Emancipação do Paraná (estadual)
};
// lojaSlug é OPCIONAL: sem ele conta só os feriados nacionais (métricas gerais, ex. meta do site).
export function isFeriado(d, lojaSlug) {
  const mmdd = String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  if (FERIADOS_FIXOS.includes(mmdd)) return true;
  if (FERIADOS_MOVEIS.includes(`${d.getFullYear()}-${mmdd}`)) return true;
  const locais = lojaSlug ? FERIADOS_LOCAIS[lojaSlug] : null;
  return !!(locais && locais.includes(mmdd));
}

export function businessDaysBetween(dataInicio, dataFim, lojaSlug) {
  if (!dataInicio || !dataFim) return 0;
  const ini = new Date(dataInicio + 'T00:00:00');
  const fim = new Date(dataFim + 'T00:00:00');
  if (fim < ini) return 0;
  let n = 0;
  const d = new Date(ini);
  while (d <= fim) {
    const wd = d.getDay(); // 0=dom, 6=sáb
    if (wd !== 0 && wd !== 6 && !isFeriado(d, lojaSlug)) n += 1;
    d.setDate(d.getDate() + 1);
  }
  return n;
}

// Dias úteis no mês inteiro a que pertence dataInicio.
export function businessDaysInMonth(dataInicio, lojaSlug) {
  const ini = new Date(dataInicio + 'T00:00:00');
  const y = ini.getFullYear();
  const m = ini.getMonth();
  const first = fmtDate(new Date(y, m, 1));
  const last = fmtDate(new Date(y, m + 1, 0));
  return businessDaysBetween(first, last, lojaSlug);
}

// Projeta o valor para o fim do mês pelo ritmo de DIAS ÚTEIS do período:
//   projeção = realizado / dias_úteis_decorridos × dias_úteis_do_mês.
// Retorna { projecao, duDecorridos, duMes, fator }. Trata divisão por zero.
export function projectByBusinessDays(realizado, dataInicio, dataFim, lojaSlug) {
  const r = Number.isFinite(realizado) ? realizado : 0;
  const duDecorridos = businessDaysBetween(dataInicio, dataFim, lojaSlug);
  const duMes = businessDaysInMonth(dataInicio, lojaSlug);
  const fator = duDecorridos > 0 ? duMes / duDecorridos : 1;
  const projecao = duDecorridos > 0 ? r * fator : r;
  return { projecao, duDecorridos, duMes, fator };
}

// ===== StoreChip simples (recriação — o original não é exportado) =====
export function StoreChip({ company, size = 'md' }) {
  const c = COMPANY_THEME[company.slug]?.color || '#9ca3af';
  const small = size === 'sm';
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: small ? 6 : 7,
        padding: small ? '3px 8px 3px 7px' : '4px 10px 4px 8px',
        background: 'rgba(15,23,42,0.04)',
        border: '1px solid rgba(15,23,42,0.08)',
        borderRadius: 999,
      }}
    >
      <span
        style={{
          width: small ? 6 : 7,
          height: small ? 6 : 7,
          borderRadius: '50%',
          background: c,
          boxShadow: `0 0 8px ${c}aa`,
        }}
      />
      <span
        style={{
          fontSize: small ? 10.5 : 11,
          fontWeight: 700,
          color: '#1e293b',
          letterSpacing: '0.02em',
        }}
      >
        {company.name}
      </span>
    </div>
  );
}

export const storeColor = (slug) => COMPANY_THEME[slug]?.color || '#9ca3af';
